import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  filterEmptyRoles,
  filterRolesToHide,
} from 'src/components/featureFlags/featureFlagFilters';
import {
  COUNTRY_CODE_US,
  GLCI_ROLES,
  REPORT_MAKER_ROLES,
  Roles,
} from 'src/constants';
import { getOrgAppRoles } from 'src/services/apiService';
import { AppRoleInfo, OrgAppRoles, RoleInfo } from 'src/types';
import useAuth from './useAuth';
import useFeatureFlags from './useFeatureFlags';

interface OrgAppRolesState {
  uid?: string | null;
  centerId?: string;
  orgAppRoles: OrgAppRoles;
  filteredAppRoles: OrgAppRoles;
  searchQuery: string;
  isLoading: number;
  error: string | null;
  fetchOrgAppRoles: (uid: string) => void;
  updateExpand: (expandAll: boolean, expand: boolean, key) => void;
  updateSearchQuery: (string) => void;
}

export const defaultOrgAppRolesContextValue = {
  uid: null,
  centerId: undefined,
  orgAppRoles: {
    appRoles: [],
  },
  filteredAppRoles: {
    appRoles: [],
  },
  searchQuery: '',
  isLoading: 0,
  error: null,
  fetchOrgAppRoles: (_uid: string) => {
    throw new Error('Not Implemented');
  },
  updateExpand: (_expandAll: boolean, _exoand: boolean, _key) => {
    throw new Error('Not Implemented');
  },
  updateSearchQuery: () => {
    throw new Error('Not Implemented');
  },
};

const OrgAppRolesContext = createContext<OrgAppRolesState>(
  defaultOrgAppRolesContextValue
);

const useOrgAppRoles = (uid: string, centerId?: string): OrgAppRolesState => {
  defaultOrgAppRolesContextValue.uid = uid;
  defaultOrgAppRolesContextValue.centerId = centerId;
  return useContext(OrgAppRolesContext);
};

export const OrgAppRolesProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const {
    user: { id, organisationId, country },
    getCurrentOrg,
    isSSHUser,
    hasSSHInParentOrg,
    hasDelegateInParentOrg,
    isDelegate,
    isInternalUser,
  } = useAuth();
  const currentOrganisation = getCurrentOrg();
  const centerType = currentOrganisation?.centerType;
  const { isEnabled } = useFeatureFlags();

  const [orgAppRoles, setOrgAppRoles] = useState<OrgAppRoles>(
    defaultOrgAppRolesContextValue.orgAppRoles
  );

  const orgAppRolesRef = useRef<OrgAppRoles>(
    defaultOrgAppRolesContextValue.orgAppRoles
  );

  const [isLoading, setIsLoading] = useState<number>(
    defaultOrgAppRolesContextValue.isLoading
  );

  const [error, setError] = useState(defaultOrgAppRolesContextValue.error);

  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchOrgAppRoles = async (uid: string, centerId?: string) => {
    try {
      setIsLoading((prev) => prev + 1);
      setError(null);
      const data = await getOrgAppRoles(centerId ?? organisationId, uid || id);
      const processedAppRoles = data.appRoles
        .filter(filterEmptyRoles(isEnabled))
        .map(filterRolesToHide(isEnabled));
      setOrgAppRoles({
        appRoles: processedAppRoles,
      });
      orgAppRolesRef.current = {
        appRoles: processedAppRoles,
      };
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  };

  const isSSHPlatform = (appRole: AppRoleInfo) =>
    appRole.platform === 'School Support Hub';

  const removeReportMakerRoles = (appRole: AppRoleInfo) =>
    !appRole.roles.some((role) =>
      REPORT_MAKER_ROLES.includes(role.name as Roles)
    );

  const removeEmptyRoles = (appRole: AppRoleInfo) => appRole.roles.length > 0;

  const isRestrictedRole = (role: RoleInfo) => role.restricted;

  const hasGLCIRole = (role: RoleInfo) => GLCI_ROLES.includes(role.name);

  const canAssignSSHCoordinator = (): boolean => {
    const viewingChildCentre = location.pathname.includes('/associate');

    // Internal delegates always have permission.
    const internalDelegate = isInternalUser && isDelegate;

    // Users with SSH rights in the parent org can assign coordinator in child centres.
    const parentSshCanAssignToChild =
      centerType === 'child' &&
      (hasSSHInParentOrg || (hasDelegateInParentOrg && isInternalUser));

    // When explicitly viewing a child centre, allow SSH users or internal delegates.
    const viewingChildWithRights =
      viewingChildCentre && (isSSHUser || (isInternalUser && isDelegate));

    return (
      internalDelegate || parentSshCanAssignToChild || viewingChildWithRights
    );
  };

  const processPlatformRoles = (appRole: AppRoleInfo) => {
    const modifiedAppRole = { ...appRole };

    // Filter out restricted roles for non-internal users
    if (!isInternalUser) {
      modifiedAppRole.roles = appRole.roles.filter(
        (role) => !isRestrictedRole(role)
      );

      // Only process Global Listening platform
      if (appRole.platform === 'Global Listening') {
        modifiedAppRole.roles = appRole.roles.filter(
          (role) => !hasGLCIRole(role)
        );
      }
    }

    // Process School Support Hub platform for SSH users
    if (isSSHPlatform(appRole)) {
      const isCoordinatorRole = (role) =>
        role.name === Roles.SCHOOL_SUPPORT_HUB_COORDINATOR;

      // Filter out the coordinator role if the user can't assign it
      if (!canAssignSSHCoordinator()) {
        modifiedAppRole.roles = modifiedAppRole.roles.filter(
          (role) => !isCoordinatorRole(role)
        );
      }
    }

    return modifiedAppRole;
  };

  const filteredAppRoles: OrgAppRoles = useMemo(() => {
    let processedAppRoles = orgAppRoles.appRoles;
    // If user is SSH user but not delegated admin, only show School Support Hub platform
    // If user has SSH in parent org but not delegated admin, only show School Support Hub platform
    if (
      (isSSHUser && !isDelegate) ||
      (hasSSHInParentOrg && !hasDelegateInParentOrg)
    ) {
      processedAppRoles = processedAppRoles.filter(isSSHPlatform);
    }

    // Filter out report maker roles for non-US users
    if (country !== COUNTRY_CODE_US) {
      processedAppRoles = processedAppRoles.filter(removeReportMakerRoles);
    }

    processedAppRoles = processedAppRoles.map(processPlatformRoles);

    // Remove any app roles that don't have any roles left after filtering
    processedAppRoles = processedAppRoles.filter(removeEmptyRoles);
    return {
      appRoles: processedAppRoles ?? [],
    };
  }, [
    orgAppRoles,
    country,
    isInternalUser,
    isSSHUser,
    isDelegate,
    centerType,
    hasSSHInParentOrg,
  ]);

  const updateExpand = (
    expandAll: boolean,
    expand: boolean,
    platformName: string
  ) => {
    if (expandAll) {
      setOrgAppRoles((prevState) => ({
        appRoles: prevState.appRoles.map((item) => {
          return { ...item, expand: !expand };
        }),
      }));
    } else {
      setOrgAppRoles((prevState) => ({
        appRoles: prevState.appRoles.map((item) => {
          if (item.platform === platformName)
            return { ...item, expand: !expand };
          return item;
        }),
      }));
    }
  };

  const searchForPlatformAndRoles = (value) => {
    const filteredData = [];
    for (const item of orgAppRolesRef.current.appRoles) {
      if (item.platform.toLowerCase().includes(value.toLowerCase())) {
        filteredData.push(item);
      } else if (
        item.roles.find((roleItem) => {
          return roleItem.alias.toLowerCase().includes(value.toLowerCase());
        })
      ) {
        const filteredRoles = item.roles.filter((roleitem) =>
          roleitem.alias.toLowerCase().includes(value.toLowerCase())
        );
        filteredData.push({ ...item, roles: filteredRoles });
      }
    }
    return filteredData;
  };

  const updateSearchQuery = useCallback(
    (value) => {
      setSearchQuery((prevState) => {
        if (prevState !== value) {
          setOrgAppRoles({
            appRoles:
              value.length === 0
                ? orgAppRolesRef.current.appRoles
                : searchForPlatformAndRoles(value),
          });
        }
        return value;
      });
    },
    [orgAppRoles]
  );

  useEffect(() => {
    fetchOrgAppRoles(
      defaultOrgAppRolesContextValue.uid,
      defaultOrgAppRolesContextValue.centerId
    );
    return () => {
      setOrgAppRoles(defaultOrgAppRolesContextValue.orgAppRoles);
      setIsLoading(defaultOrgAppRolesContextValue.isLoading);
      setError(defaultOrgAppRolesContextValue.error);
    };
  }, [organisationId, isEnabled]);

  return (
    <OrgAppRolesContext.Provider
      value={{
        orgAppRoles,
        filteredAppRoles,
        searchQuery,
        isLoading,
        error,
        fetchOrgAppRoles,
        updateExpand,
        updateSearchQuery,
      }}
    >
      {children}
    </OrgAppRolesContext.Provider>
  );
};

export default useOrgAppRoles;
